import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef } from 'react'

import { type CreditCardItem } from '@webapp/modules/accounts-payable/entities/CreditCardItem'
import { useUnmatchedCreditCardItemListPresenter } from './useUnmatchedCreditCardItemListPresenter'
import { UnmatchedCreditCardItemsPane } from '../../views/UnmatchedCreditCardItemsPane'

export interface IUnmatchedCreditCardItemListPresenterProps {
  accountId: number
  /** Card currency for the "Invalid currency" badge (BHV-032) */
  cardCurrencyId: Nullable<number>
  /** Selection bridge — the FS computes selection variance (ADR-9, BHV-031) */
  selectedItemIds: number[]
  onSelectionChange: (items: CreditCardItem[]) => void
  /** Filters the sibling statement pane (cross-feature, BHV-037) */
  onShowSuggestedMatches: (item: CreditCardItem) => void
  /** Slot for inline-edit-credit-card-item (ADR-9) */
  renderInlineCostCell: (item: CreditCardItem) => ReactNode
  /** Slot for the Edit / View audit log / Remove menu (ADR-9) */
  renderRowMenu: (item: CreditCardItem) => ReactNode
}

export const UnmatchedCreditCardItemListPresenter = ({
  accountId,
  cardCurrencyId,
  selectedItemIds,
  onSelectionChange,
  onShowSuggestedMatches,
  renderInlineCostCell,
  renderRowMenu,
}: IUnmatchedCreditCardItemListPresenterProps) => {
  const {
    data: { items },
    states: { isLoading },
  } = useUnmatchedCreditCardItemListPresenter({ accountId })

  // Pool of every item seen (survives pagination) so selected ids can always
  // be resolved back to entities for the FS selection bridge.
  const poolRef = useRef(new Map<number, CreditCardItem>())
  useEffect(() => {
    items.forEach((item) => poolRef.current.set(item.id, item))
  }, [items])

  const lastClickedIndexRef = useRef<Nullable<number>>(null)

  // Click toggles; shift-click extends from the last clicked row (BHV-031).
  const handleRowClick = useCallback(
    (item: CreditCardItem, index: number, isShiftKey: boolean) => {
      let nextIds: number[]

      if (isShiftKey && lastClickedIndexRef.current !== null) {
        const start = Math.min(lastClickedIndexRef.current, index)
        const end = Math.max(lastClickedIndexRef.current, index)
        const rangeIds = items.slice(start, end + 1).map((row) => row.id)
        nextIds = Array.from(new Set([...selectedItemIds, ...rangeIds]))
      } else if (selectedItemIds.includes(item.id)) {
        nextIds = selectedItemIds.filter((id) => id !== item.id)
      } else {
        nextIds = [...selectedItemIds, item.id]
      }

      lastClickedIndexRef.current = index

      onSelectionChange(
        nextIds.reduce<CreditCardItem[]>((selected, id) => {
          const entity = poolRef.current.get(id)
          return entity ? [...selected, entity] : selected
        }, [])
      )
    },
    [items, onSelectionChange, selectedItemIds]
  )

  return (
    <UnmatchedCreditCardItemsPane
      items={items}
      isLoading={isLoading}
      selectedItemIds={selectedItemIds}
      cardCurrencyId={cardCurrencyId}
      onRowClick={handleRowClick}
      onShowSuggestedMatches={onShowSuggestedMatches}
      renderInlineCostCell={renderInlineCostCell}
      renderRowMenu={renderRowMenu}
    />
  )
}

UnmatchedCreditCardItemListPresenter.displayName =
  'UnmatchedCreditCardItemListPresenter'
